import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { ThemeToggle } from '../components/ThemeToggle';
import { FMSBodyMap } from '../components/FMSBodyMap';
import { computeRegionStatuses } from '../lib/fmsBodyRegions';
import { ArrowLeft, Plus, AlertTriangle, Trash2, ChevronRight, Activity, Info } from 'lucide-react';
import axios from 'axios';
import { useConfirm } from '../contexts/ConfirmContext';
import { toast } from 'sonner';

import { localISODate } from '../lib/utils';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const RISK_COLORS = {
  low: { text: 'text-green-500', label: 'NISKIE RYZYKO', bg: 'bg-green-500/10', border: 'border-green-500/40' },
  moderate: { text: 'text-yellow-500', label: 'UMIARKOWANE', bg: 'bg-yellow-500/10', border: 'border-yellow-500/40' },
  high: { text: 'text-red-500', label: 'WYSOKIE RYZYKO', bg: 'bg-red-500/10', border: 'border-red-500/40' },
  unknown: { text: 'text-[#A1A1A1]', label: 'BRAK', bg: 'bg-[#1E1E1E]', border: 'border-[#2A2A2A]' },
};

export const FMSPage = () => {
  const confirm = useConfirm();
  const { id: clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [assessments, setAssessments] = useState([]);
  const [standardTests, setStandardTests] = useState([]);
  const [customTests, setCustomTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('list'); // 'list' | 'new' | 'detail'
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [newCustomDialogOpen, setNewCustomDialogOpen] = useState(false);
  const [customForm, setCustomForm] = useState({ name: '', instruction: '' });
  // assessment form state
  const [assessmentForm, setAssessmentForm] = useState({
    assessment_date: localISODate(),
    tests: {}, // { test_id: { score, pain_flag, asymmetry_flag, notes } }
    general_notes: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchAll(); }, [clientId]);

  const fetchAll = async () => {
    try {
      const [c, a, s, ct] = await Promise.all([
        axios.get(`${API_URL}/api/clients/${clientId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/fms/assessments/client/${clientId}`, { withCredentials: true }),
        axios.get(`${API_URL}/api/fms/standard-tests`, { withCredentials: true }),
        axios.get(`${API_URL}/api/fms/custom-tests`, { withCredentials: true }),
      ]);
      setClient(c.data);
      setAssessments(a.data);
      setStandardTests(s.data);
      setCustomTests(ct.data);
    } catch (e) {
      console.error('FMS fetch error:', e);
    } finally {
      setLoading(false);
    }
  };

  const allTests = [
    ...standardTests.map(t => ({ ...t, is_custom: false })),
    ...customTests.map(t => ({ id: t.id, name: t.name, instruction: t.instruction, is_custom: true })),
  ];

  const updateTestField = (testId, field, value) => {
    setAssessmentForm(prev => ({
      ...prev,
      tests: {
        ...prev.tests,
        [testId]: { ...(prev.tests[testId] || { score: 3, pain_flag: false, asymmetry_flag: false, notes: '' }), [field]: value }
      }
    }));
  };

  const handleSaveAssessment = async () => {
    setSaving(true);
    try {
      const tests = allTests
        .filter(t => assessmentForm.tests[t.id])
        .map(t => ({
          test_id: t.id,
          test_name: t.name,
          is_custom: t.is_custom,
          score: assessmentForm.tests[t.id].pain_flag ? 0 : (assessmentForm.tests[t.id].score ?? 3),
          pain_flag: !!assessmentForm.tests[t.id].pain_flag,
          asymmetry_flag: !!assessmentForm.tests[t.id].asymmetry_flag,
          notes: assessmentForm.tests[t.id].notes || '',
        }));
      if (tests.length === 0) {
        toast.error('Oceń przynajmniej jeden test przed zapisem');
        setSaving(false);
        return;
      }
      const res = await axios.post(`${API_URL}/api/fms/assessments`, {
        client_id: clientId,
        assessment_date: assessmentForm.assessment_date,
        tests,
        general_notes: assessmentForm.general_notes,
      }, { withCredentials: true });
      setSelectedAssessment(res.data);
      setAssessments([res.data, ...assessments]);
      setMode('detail');
      setAssessmentForm({ assessment_date: localISODate(), tests: {}, general_notes: '' });
    } catch (e) {
      toast.error('Błąd zapisu oceny: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateCustom = async () => {
    try {
      const res = await axios.post(`${API_URL}/api/fms/custom-tests`, {
        name: customForm.name, instruction: customForm.instruction, max_score: 3,
      }, { withCredentials: true });
      setCustomTests([...customTests, res.data]);
      setCustomForm({ name: '', instruction: '' });
      setNewCustomDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Błąd');
    }
  };

  const handleDeleteCustom = async (testId) => {
    if (!await confirm('Usunąć ten test?')) return;
    try {
      await axios.delete(`${API_URL}/api/fms/custom-tests/${testId}`, { withCredentials: true });
      setCustomTests(customTests.filter(t => t.id !== testId));
    } catch (e) { /* ignore */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-white">Ładowanie...</div>;

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="fms-page">
      <nav className="border-b border-[#2A2A2A] px-4 sm:px-8 py-4 sm:py-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-2">
          <Logo size="md" />
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <Button onClick={() => navigate(`/client/${clientId}`)} variant="ghost" className="text-[#A1A1A1] hover:text-white px-2 sm:px-4" data-testid="back-to-client">
              <ArrowLeft className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Powrót</span>
            </Button>
            <ThemeToggle />
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-12">
        <div className="mb-10">
          <div className="flex items-center gap-3 mb-2">
            <Activity className="w-7 h-7 text-[#C9A84C]" />
            <h1 className="display-text text-4xl text-white">Ocena Funkcjonalna (FMS)</h1>
          </div>
          <p className="body-text text-[#A1A1A1] text-lg">{client?.name} — Functional Movement Screen</p>
        </div>

        {/* MODE: LIST */}
        {mode === 'list' && (
          <div className="space-y-8">
            {assessments.length > 0 && (
              <div className="card-surface rounded-3xl p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="heading-text text-2xl text-white">Mapa ciała — aktualny stan</h2>
                  <span className="text-xs text-[#A1A1A1]">
                    Na podstawie oceny z {new Date(assessments[0].assessment_date).toLocaleDateString('pl-PL')}
                  </span>
                </div>
                <FMSBodyMap regionStatuses={computeRegionStatuses(assessments[0].tests)} gender={client?.gender} />
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="heading-text text-2xl text-white">Historia ocen</h2>
              <Button
                onClick={() => setMode('new')}
                className="bg-transparent border border-[#C9A84C] text-white hover:bg-[#C9A84C]/10 rounded-full px-6"
                data-testid="new-assessment-button"
              >
                <Plus className="w-4 h-4 mr-2" /> Nowa Ocena
              </Button>
            </div>

            {assessments.length === 0 ? (
              <div className="card-surface rounded-3xl p-12 text-center">
                <Activity className="w-12 h-12 text-[#A1A1A1] mx-auto mb-4" />
                <p className="body-text text-[#A1A1A1]">Brak ocen. Utwórz pierwszą ocenę funkcjonalną.</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {assessments.map(a => {
                  const risk = RISK_COLORS[a.risk_level] || RISK_COLORS.unknown;
                  return (
                    <button
                      key={a.id}
                      onClick={() => { setSelectedAssessment(a); setMode('detail'); }}
                      className={`card-surface rounded-3xl p-6 text-left transition-all hover:border-[#C9A84C]/40 ${risk.border} border`}
                      data-testid={`assessment-row-${a.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="label-text text-[#A1A1A1] mb-1">DATA</div>
                          <div className="text-xl text-white">{new Date(a.assessment_date).toLocaleDateString('pl-PL')}</div>
                        </div>
                        <div className="flex items-center gap-8">
                          <div>
                            <div className="label-text text-[#A1A1A1] mb-1">WYNIK</div>
                            <div className="text-2xl text-[#C9A84C] font-bold">{a.total_score} / {a.tests.length * 3}</div>
                          </div>
                          <div>
                            <div className="label-text text-[#A1A1A1] mb-1">RYZYKO</div>
                            <div className={`text-sm font-medium ${risk.text}`}>{risk.label}</div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-[#A1A1A1]" />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Custom tests management */}
            <div className="card-surface rounded-3xl p-8">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="heading-text text-xl text-white mb-1">Własne testy</h3>
                  <p className="text-sm text-[#A1A1A1]">Możesz dodać maksymalnie 3 własne testy oprócz 7 standardowych FMS</p>
                </div>
                <Dialog open={newCustomDialogOpen} onOpenChange={setNewCustomDialogOpen}>
                  <DialogTrigger asChild>
                    <Button
                      disabled={customTests.length >= 3}
                      className="bg-transparent border border-[#C9A84C] text-white hover:bg-[#C9A84C]/10 rounded-full"
                      data-testid="add-custom-test-button"
                    >
                      <Plus className="w-4 h-4 mr-2" /> Dodaj test ({customTests.length}/3)
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-[#1E1E1E] border-[#2A2A2A] text-white">
                    <DialogHeader><DialogTitle>Nowy test własny</DialogTitle></DialogHeader>
                    <div className="space-y-4 mt-2">
                      <div>
                        <Label className="label-text text-[#A1A1A1] mb-2 block">NAZWA</Label>
                        <Input value={customForm.name} onChange={e => setCustomForm({...customForm, name: e.target.value})} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl" data-testid="custom-test-name" />
                      </div>
                      <div>
                        <Label className="label-text text-[#A1A1A1] mb-2 block">INSTRUKCJA</Label>
                        <Textarea rows={4} value={customForm.instruction} onChange={e => setCustomForm({...customForm, instruction: e.target.value})} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl" data-testid="custom-test-instruction" />
                      </div>
                      <Button onClick={handleCreateCustom} disabled={!customForm.name || !customForm.instruction} className="w-full bg-transparent border border-[#C9A84C] text-white rounded-full" data-testid="custom-test-save">Zapisz</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              {customTests.length === 0 ? (
                <p className="text-sm text-[#A1A1A1]">Brak własnych testów.</p>
              ) : (
                <div className="space-y-2">
                  {customTests.map(t => (
                    <div key={t.id} className="flex items-start justify-between gap-4 p-4 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                      <div className="flex-1">
                        <div className="text-white font-medium">{t.name}</div>
                        <div className="text-sm text-[#A1A1A1] mt-1">{t.instruction}</div>
                      </div>
                      <button onClick={() => handleDeleteCustom(t.id)} className="text-[#A1A1A1] hover:text-red-400" aria-label="Usuń">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* MODE: NEW assessment */}
        {mode === 'new' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <button onClick={() => setMode('list')} className="text-sm text-[#A1A1A1] hover:text-white flex items-center gap-2">
                <ArrowLeft className="w-4 h-4" /> Anuluj
              </button>
              <div className="text-sm text-[#A1A1A1]">{allTests.length} testów dostępnych</div>
            </div>

            <div className="card-surface rounded-3xl p-6">
              <Label className="label-text text-[#A1A1A1] mb-2 block">DATA OCENY</Label>
              <Input type="date" value={assessmentForm.assessment_date} onChange={e => setAssessmentForm({...assessmentForm, assessment_date: e.target.value})} className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white max-w-xs" data-testid="assessment-date" />
            </div>

            {allTests.map((t, idx) => {
              const testState = assessmentForm.tests[t.id] || { score: 3, pain_flag: false, asymmetry_flag: false, notes: '' };
              const isActive = !!assessmentForm.tests[t.id];
              return (
                <div key={t.id} className={`card-surface rounded-3xl p-6 border ${isActive ? 'border-[#C9A84C]/50' : 'border-[#2A2A2A]'}`} data-testid={`test-card-${t.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="label-text text-[#C9A84C] text-xs">TEST {idx + 1}{t.is_custom && ' · WŁASNY'}</span>
                      </div>
                      <h3 className="heading-text text-xl text-white mb-2">{t.name}</h3>
                      <div className="flex items-start gap-2 text-sm text-[#A1A1A1]">
                        <Info className="w-4 h-4 mt-0.5 shrink-0" />
                        <p>{t.instruction}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-5 gap-4 items-start">
                    {/* Score buttons */}
                    <div className="md:col-span-2">
                      <Label className="label-text text-[#A1A1A1] mb-2 block">WYNIK (0-3)</Label>
                      <div className="flex gap-2">
                        {[0, 1, 2, 3].map(s => (
                          <button
                            key={s}
                            disabled={testState.pain_flag && s !== 0}
                            onClick={() => updateTestField(t.id, 'score', s)}
                            className={`w-12 h-12 rounded-full border text-lg font-bold transition-all ${
                              isActive && testState.score === s && !testState.pain_flag
                                ? 'bg-[#C9A84C] border-[#C9A84C] text-[#0A0A0A]'
                                : 'bg-transparent border-[#2A2A2A] text-white hover:border-[#C9A84C]/50'
                            } ${testState.pain_flag && s !== 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                            data-testid={`score-${t.id}-${s}`}
                          >
                            {s}
                          </button>
                        ))}
                      </div>
                    </div>
                    {/* Pain flag */}
                    <div>
                      <Label className="label-text text-[#A1A1A1] mb-2 block">BÓL</Label>
                      <button
                        onClick={() => {
                          const newPain = !testState.pain_flag;
                          updateTestField(t.id, 'pain_flag', newPain);
                          if (newPain) updateTestField(t.id, 'score', 0);
                        }}
                        className={`px-4 h-12 rounded-full border text-sm font-medium transition-all w-full ${
                          testState.pain_flag
                            ? 'bg-red-500/20 border-red-500 text-red-400'
                            : 'bg-transparent border-[#2A2A2A] text-[#A1A1A1] hover:text-white'
                        }`}
                        data-testid={`pain-${t.id}`}
                      >
                        <AlertTriangle className="w-4 h-4 inline mr-1" /> {testState.pain_flag ? 'Ból (wynik 0)' : 'Brak bólu'}
                      </button>
                    </div>
                    {/* Asymmetry */}
                    <div>
                      <Label className="label-text text-[#A1A1A1] mb-2 block">ASYMETRIA L/P</Label>
                      <button
                        onClick={() => updateTestField(t.id, 'asymmetry_flag', !testState.asymmetry_flag)}
                        className={`px-4 h-12 rounded-full border text-sm font-medium transition-all w-full ${
                          testState.asymmetry_flag
                            ? 'bg-yellow-500/20 border-yellow-500 text-yellow-400'
                            : 'bg-transparent border-[#2A2A2A] text-[#A1A1A1] hover:text-white'
                        }`}
                        data-testid={`asym-${t.id}`}
                      >
                        {testState.asymmetry_flag ? 'TAK' : 'Brak'}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <Label className="label-text text-[#A1A1A1] mb-2 block">NOTATKA</Label>
                    <Textarea
                      rows={2}
                      placeholder="Obserwacje, kompensacje, korekty..."
                      value={testState.notes}
                      onChange={e => updateTestField(t.id, 'notes', e.target.value)}
                      className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white text-sm"
                      data-testid={`notes-${t.id}`}
                    />
                  </div>
                </div>
              );
            })}

            <div className="card-surface rounded-3xl p-6">
              <Label className="label-text text-[#A1A1A1] mb-2 block">NOTATKI OGÓLNE</Label>
              <Textarea
                rows={3}
                placeholder="Ogólne uwagi do całej oceny..."
                value={assessmentForm.general_notes}
                onChange={e => setAssessmentForm({...assessmentForm, general_notes: e.target.value})}
                className="bg-[#0A0A0A] border-[#2A2A2A] rounded-2xl text-white"
                data-testid="general-notes"
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button onClick={() => setMode('list')} variant="ghost" className="text-[#A1A1A1] hover:text-white">Anuluj</Button>
              <Button
                onClick={handleSaveAssessment}
                disabled={saving}
                className="bg-transparent border border-[#C9A84C] text-white hover:bg-[#C9A84C]/10 rounded-full px-8"
                data-testid="save-assessment"
              >
                {saving ? 'Zapisywanie...' : 'Zapisz ocenę'}
              </Button>
            </div>
          </div>
        )}

        {/* MODE: DETAIL */}
        {mode === 'detail' && selectedAssessment && (
          <div className="space-y-6">
            <button onClick={() => { setMode('list'); setSelectedAssessment(null); }} className="text-sm text-[#A1A1A1] hover:text-white flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" /> Powrót do historii
            </button>

            <div className={`card-surface rounded-3xl p-8 border ${RISK_COLORS[selectedAssessment.risk_level].border}`}>
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-text text-[#A1A1A1] mb-1">DATA OCENY</div>
                  <div className="text-2xl text-white">{new Date(selectedAssessment.assessment_date).toLocaleDateString('pl-PL')}</div>
                </div>
                <div className="text-right">
                  <div className="text-5xl font-bold text-[#C9A84C]">{selectedAssessment.total_score}</div>
                  <div className="text-sm text-[#A1A1A1]">/ {selectedAssessment.tests.length * 3}</div>
                </div>
              </div>
              <div className={`inline-block px-4 py-2 rounded-full text-sm font-medium ${RISK_COLORS[selectedAssessment.risk_level].text} ${RISK_COLORS[selectedAssessment.risk_level].bg}`}>
                {RISK_COLORS[selectedAssessment.risk_level].label}
              </div>
              {selectedAssessment.general_notes && (
                <div className="mt-6 p-4 bg-[#0A0A0A] rounded-2xl border border-[#2A2A2A]">
                  <div className="label-text text-[#A1A1A1] mb-2">NOTATKI OGÓLNE</div>
                  <p className="body-text text-white text-sm whitespace-pre-wrap">{selectedAssessment.general_notes}</p>
                </div>
              )}
            </div>

            <div className="card-surface rounded-3xl p-8">
              <h3 className="heading-text text-xl text-white mb-6">Mapa ciała</h3>
              <FMSBodyMap regionStatuses={computeRegionStatuses(selectedAssessment.tests)} gender={client?.gender} />
            </div>

            <div className="space-y-3">
              {selectedAssessment.tests.map((t, i) => (
                <div key={i} className={`card-surface rounded-2xl p-5 border ${t.pain_flag ? 'border-red-500/40' : 'border-[#2A2A2A]'}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-white font-medium">{t.test_name}{t.is_custom && <span className="ml-2 text-xs text-[#C9A84C]">WŁASNY</span>}</div>
                      {t.notes && <div className="text-sm text-[#A1A1A1] mt-1">{t.notes}</div>}
                      <div className="flex gap-2 mt-2">
                        {t.pain_flag && <span className="text-xs px-2 py-1 rounded-full bg-red-500/20 text-red-400">BÓL</span>}
                        {t.asymmetry_flag && <span className="text-xs px-2 py-1 rounded-full bg-yellow-500/20 text-yellow-400">ASYMETRIA</span>}
                      </div>
                    </div>
                    <div className="text-3xl font-bold text-[#C9A84C] ml-6">{t.score}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
